import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  setCurrentStep,
  selectCurrentStep,
  selectStep1Data,
  selectStep2Data,
  selectStep3Data,
  selectIsOrganizationCreated,
  createOrganization,
  updateOrganization,
  fetchOrganizationById,
} from "@/store/redux/slices/kyc/kyc_setup_slice";
import { uploadFile } from "@/store/redux/slices/user/file_upload_slice";
import { getUserId, getOrganizationId } from "@/utils/token_functions";
import Button from "@/components/common/Button";
import { CheckOutlined } from "@/config/antd";
import Copyright from "@/components/layout/Footer/Copyright";
import { getFirstName } from "@/utils/nameUtils";
import "@/assets/styles/custom_check.css";

const isStep1Filled = (step1 = {}) =>
  Boolean(
    step1.name && step1.type && step1.address && step1.email && step1.phone
  );

const isStep2Filled = (step2 = {}) =>
  Boolean(
    step2.ownerFullName &&
      step2.ownerEmail &&
      step2.ownerPhone &&
      step2.modeOfIdentification &&
      step2.identificationImage
  );

const isStep3Filled = (step3 = {}) =>
  Boolean(
    step3.CACRegistrationCertificateNumber &&
      step3.CACRegistrationCertificateImage &&
      step3.modeOfIdentification &&
      step3.identificationImage
  );

const AccountSetupReview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();

  // Get all necessary data from Redux
  const currentStep = useSelector(selectCurrentStep);
  const step1Data = useSelector(selectStep1Data);
  const step2Data = useSelector(selectStep2Data);
  const step3Data = useSelector(selectStep3Data);
  const userInfo = useSelector((state) => state.auth.user);
  const isOrganizationCreated = useSelector(selectIsOrganizationCreated);

  getFirstName(userInfo?.fullName);

  const allSteps = [
    {
      step: 1,
      name: "Company Information",
      isComplete: isStep1Filled(step1Data),
    },
    {
      step: 2,
      name: "Owner's Information",
      isComplete: isStep2Filled(step2Data),
    },
    {
      step: 3,
      name: "Business Information",
      isComplete: isStep3Filled(step3Data),
    },
  ];

  const incompleteSteps = allSteps.filter((step) => !step.isComplete);
  const completeSteps = allSteps.filter((step) => step.isComplete);

  const handleNavigateToStep = (step) => {
    dispatch(setCurrentStep(step));
    navigate(`/account-setup-step-${step}`);
  };

  const handleSubmission = async () => {
    if (incompleteSteps.length === 0) {
      showLoader();
      try {
        const userID = getUserId();
        let filesToUpload = [];

        const isBase64 = (str) => {
          if (typeof str !== "string") return false;
          return str.startsWith("data:");
        };

        const isURL = (str) => {
          if (typeof str !== "string") return false;
          return str.startsWith("http");
        };

        const base64ToFile = async (base64String, fileName) => {
          if (!base64String || !base64String.startsWith("data:")) return null;
          try {
            const response = await fetch(base64String);
            const blob = await response.blob();
            return new File([blob], fileName, { type: blob.type });
          } catch (error) {
            console.error("Error converting base64 to file:", error);
            return null;
          }
        };

        if (step1Data.ownerProfileImage) {
          if (step1Data.ownerProfileImage instanceof File) {
            filesToUpload.push(step1Data.ownerProfileImage);
          } else if (isBase64(step1Data.ownerProfileImage)) {
            const profileFile = await base64ToFile(
              step1Data.ownerProfileImage,
              "profile-image.jpg"
            );
            if (profileFile) filesToUpload.push(profileFile);
          } else if (isURL(step1Data.ownerProfileImage)) {
            // URL case - no action needed
          }
        }

        if (step2Data.identificationImage) {
          if (step2Data.identificationImage instanceof File) {
            filesToUpload.push(step2Data.identificationImage);
          } else if (isBase64(step2Data.identificationImage)) {
            const idFile = await base64ToFile(
              step2Data.identificationImage,
              "owner-id.jpg"
            );
            if (idFile) filesToUpload.push(idFile);
          } else if (isURL(step2Data.identificationImage)) {
            // URL case - no action needed
          }
        }

        if (step3Data.CACRegistrationCertificateImage) {
          if (step3Data.CACRegistrationCertificateImage instanceof File) {
            filesToUpload.push(step3Data.CACRegistrationCertificateImage);
          } else if (isBase64(step3Data.CACRegistrationCertificateImage)) {
            const cacFile = await base64ToFile(
              step3Data.CACRegistrationCertificateImage,
              "cac-cert.jpg"
            );
            if (cacFile) filesToUpload.push(cacFile);
          } else if (isURL(step3Data.CACRegistrationCertificateImage)) {
            // URL case - no action needed
          }
        }

        if (step3Data.identificationImage) {
          if (step3Data.identificationImage instanceof File) {
            filesToUpload.push(step3Data.identificationImage);
          } else if (isBase64(step3Data.identificationImage)) {
            const businessIdFile = await base64ToFile(
              step3Data.identificationImage,
              "business-id.jpg"
            );
            if (businessIdFile) filesToUpload.push(businessIdFile);
          } else if (isURL(step3Data.identificationImage)) {
            // URL case - no action needed
          }
        }

        // Upload all files in a single request if there are any
        let uploadedFiles = [];
        if (filesToUpload.length > 0) {
          try {
            const uploadResult = await dispatch(
              uploadFile({
                file: filesToUpload,
              })
            ).unwrap();

            uploadedFiles = Array.isArray(uploadResult) ? uploadResult : [];
          } catch (error) {
            console.error("Error uploading files:", error);
            uploadedFiles = [];
          }
        }

        // Map uploaded files to their respective fields - safely access with optional chaining
        let fileIndex = 0;
        const organizationData = {
          name: step1Data.name,
          type: step1Data.type,
          address: step1Data.address,
          email: step1Data.email,
          phone: step1Data.phone,
          role: step1Data.role,
          ownerProfileImage:
            step1Data.ownerProfileImage instanceof File ||
            isBase64(step1Data.ownerProfileImage)
              ? uploadedFiles[fileIndex++]?.fileUrl || null
              : isURL(step1Data.ownerProfileImage)
              ? step1Data.ownerProfileImage
              : null,

          ownerFullName: step2Data.ownerFullName,
          ownerEmail: step2Data.ownerEmail,
          ownerPhone: step2Data.ownerPhone,
          isOwner: true,
          ownerID: userID,
          ownerIdentificationType: step2Data.modeOfIdentification,
          ownerIdentificationImage:
            step2Data.identificationImage instanceof File ||
            isBase64(step2Data.identificationImage)
              ? uploadedFiles[fileIndex++]?.fileUrl || null
              : isURL(step2Data.identificationImage)
              ? step2Data.identificationImage
              : null,

          CACRegistrationCertificateNumber:
            step3Data.CACRegistrationCertificateNumber,
          CACRegistrationCertificateImage:
            step3Data.CACRegistrationCertificateImage instanceof File ||
            isBase64(step3Data.CACRegistrationCertificateImage)
              ? uploadedFiles[fileIndex++]?.fileUrl || null
              : isURL(step3Data.CACRegistrationCertificateImage)
              ? step3Data.CACRegistrationCertificateImage
              : null,
          modeOfIdentification: step3Data.modeOfIdentification,
          identificationImage:
            step3Data.identificationImage instanceof File ||
            isBase64(step3Data.identificationImage)
              ? uploadedFiles[fileIndex++]?.fileUrl || null
              : isURL(step3Data.identificationImage)
              ? step3Data.identificationImage
              : null,

          userID,
        };

        const existingOrgId = getOrganizationId();
        let usedUpdate = Boolean(isOrganizationCreated || existingOrgId);

        let resultAction = usedUpdate
          ? await dispatch(updateOrganization(organizationData))
          : await dispatch(createOrganization(organizationData));

        if (usedUpdate && updateOrganization.rejected.match(resultAction)) {
          const err = resultAction.payload;
          const isForbidden =
            err?.statusCode === 403 ||
            err?.error === "Forbidden" ||
            /permission/i.test(String(err?.message || ""));

          if (isForbidden) {
            usedUpdate = false;
            resultAction = await dispatch(createOrganization(organizationData));
          }
        }

        const fulfilled = usedUpdate
          ? updateOrganization.fulfilled.match(resultAction)
          : createOrganization.fulfilled.match(resultAction);

        if (fulfilled) {
          // Refresh authoritative org record so Settings KYC status sees CAC/docs.
          try {
            await dispatch(fetchOrganizationById({ force: true })).unwrap();
          } catch (refreshError) {
            if (refreshError?.name !== "ConditionError") {
              console.error(
                "Review submit — org refresh failed",
                refreshError
              );
            }
          }
          navigate("/account-setup-success");
        }
      } catch (error) {
        console.error("Error submitting organization data:", error);
      } finally {
        hideLoader();
      }
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen text-left bg-white rounded-lg lg:border lg:p-5"
      data-testid="account-setup-review">
      <h1 className="mb-2 text-xl font-bold text-left whitespace-nowrap text-darkGrey">
        Review and Submit
      </h1>
      <p className="mb-4 text-left text-small text-darkGrey">
        Make sure the information you submit here is accurate. Incomplete
        information or documentation can delay activation of your business
      </p>

      {incompleteSteps.length > 0 && (
        <div className="mb-6">
          <h1 className="mb-4 text-xl font-bold text-left text-black">
            Incomplete Section
          </h1>
          {incompleteSteps.map((step) => (
            <div
              key={step.step}
              className="flex items-center justify-between py-5 mb-2">
              <span>{step.name}</span>
              <Button
                text="Click to Complete"
                onClick={() => handleNavigateToStep(step.step)}
                color="primary"
                size="small"
                className="rounded-3xl px-3 py-[10px] text-small"
              />
            </div>
          ))}
        </div>
      )}

      {completeSteps.length > 0 && (
        <div className="mb-6">
          <h2 className="mt-10 mb-4 text-xl font-bold text-left text-black">
            Completed Sections
          </h2>
          {completeSteps.map((step) => (
            <div
              key={step.step}
              className="flex items-center justify-between py-5 mb-2 border-b">
              <span>{step.name}</span>
              <div className="styled-check-wrapper">
                <CheckOutlined />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between gap-2 mt-6">
        <Button
          text="Back"
          onClick={() => handleNavigateToStep(currentStep - 1)}
          customBgColor="secondary"
          customTextColor="primary"
          className="w-1/2 px-4 py-2 text-regular text-primary whitespace-nowrap"
        />
        <Button
          text="Proceed with Submission"
          data-testid="kyc-submit-button"
          onClick={handleSubmission}
          isActive={true}
          color="primary"
          className="w-1/2 px-4 py-2 text-regular whitespace-nowrap bg-primary text-white"
        />
      </div>

      <div className="mt-auto">
        <Copyright />
      </div>
    </div>
  );
};

export default AccountSetupReview;
